import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Drawer, Tag } from "antd";
import { CompassOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { ApiProblem } from "../../../api/httpClient";
import { EmptyState } from "../../../components/EmptyState";
import { ErrorState } from "../../../components/ErrorState";
import { ForbiddenState } from "../../../components/ForbiddenState";
import { LoadingSkeleton } from "../../../components/LoadingSkeleton";
import "./gisAdmin.css";
import {
  getCemetery,
  getCemeteryMap,
  type CemeteryData,
  type CemeteryMapData,
  type GravePositionProperties,
} from "./gisApi";
import { GisDetailRail } from "./GisDetailRail";
import { GisFilterBar } from "./GisFilterBar";
import { GisMapCanvas } from "./GisMapCanvas";
import { GisTableView } from "./GisTableView";
import { EditPositionModal } from "./EditPositionModal";
import { PublicPreviewModal } from "./PublicPreviewModal";
import { ModelGeoReferenceModal } from "../georef/ModelGeoReferenceModal";
import type { GisIdentityStatus, GisPositionState } from "./gisVocabulary";

function isForbiddenError(error: unknown): boolean {
  if (error instanceof ApiProblem && error.status === 403) return true;
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: unknown }).status === 403
  ) {
    return true;
  }
  return false;
}

function extractRequestId(error: unknown): string | undefined {
  if (error instanceof ApiProblem) return error.problem.requestId;
  if (typeof error === "object" && error !== null && "problem" in error) {
    return (error as { problem?: { requestId?: string } }).problem?.requestId;
  }
  return undefined;
}

/**
 * Bản đồ toạ độ & sơ đồ mộ (SCR-034).
 *
 * Hai chế độ dùng CHUNG một thân: `embedded=false` là trang riêng (có breadcrumb + tiêu đề trang);
 * `embedded=true` là khi được nhúng vào tab "Bản đồ" của hồ sơ nghĩa trang — lúc đó breadcrumb và
 * tiêu đề bị bỏ vì trang cha đã có, nhưng toàn bộ lọc/canvas/rail/modal giữ nguyên. Không tách
 * thành hai component: tách ra là hai bản sao của cùng một bộ lọc, rồi chúng trôi khỏi nhau.
 */
export function GisMapPage({ embedded = false, cemeteryId: cemeteryIdProp }: {
  readonly embedded?: boolean;
  /** Đường dẫn kỹ thuật; mặc định lấy từ route param (route hồ sơ cũng có `:cemeteryId`). */
  readonly cemeteryId?: string;
} = {}) {
  const params = useParams<{ cemeteryId: string }>();
  const cemeteryId = cemeteryIdProp ?? params.cemeteryId ?? "";
  const navigate = useNavigate();

  /**
   * Bề mặt CHỈ ĐỌC = tab "Bản đồ" nhúng trong hồ sơ nghĩa trang. Route riêng vẫn là bản biên
   * tập. Khai một chỗ rồi truyền xuống, để "chỗ nào cũng nhớ" không phải là một giả định.
   */
  const readOnly = embedded;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [cemetery, setCemetery] = useState<CemeteryData | null>(null);
  const [mapData, setMapData] = useState<CemeteryMapData | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [identityFilters, setIdentityFilters] = useState<Record<GisIdentityStatus, boolean>>({
    KNOWN: true,
    PARTIAL: true,
    UNKNOWN: true,
  });
  const [positionState, setPositionState] = useState<GisPositionState | "ALL">("ALL");
  const [isTableView, setIsTableView] = useState(false);

  // Selected feature
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  // Modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPublicModalOpen, setIsPublicModalOpen] = useState(false);
  const [isGeoRefModalOpen, setIsGeoRefModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!cemeteryId) return;
    setLoading(true);
    setError(null);
    try {
      const [cemeteryRes, mapRes] = await Promise.all([
        getCemetery(cemeteryId),
        getCemeteryMap(cemeteryId),
      ]);
      setCemetery(cemeteryRes);
      setMapData(mapRes);

      // Mặc định chọn mộ đầu tiên nếu có
      const graveLayer = mapRes.layers.find((l) => l.layerCode === "GRAVE_POSITION");
      const firstGrave = graveLayer?.featureCollection?.features?.[0];
      if (firstGrave) {
        setSelectedFeatureId(firstGrave.id ?? null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [cemeteryId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const allGraves = useMemo(() => {
    const layer = mapData?.layers.find((l) => l.layerCode === "GRAVE_POSITION");
    return layer?.featureCollection?.features ?? [];
  }, [mapData]);

  const mapCenter = useMemo<[number, number] | undefined>(() => {
    const boundaryLayer = mapData?.layers.find((l) => l.layerCode === "CEMETERY_BOUNDARY");
    const geom = boundaryLayer?.featureCollection?.features?.[0]?.geometry;
    if (geom?.type === "Polygon" && Array.isArray(geom.coordinates?.[0])) {
      const coords = geom.coordinates[0] as unknown as number[][];
      if (Array.isArray(coords) && coords.length > 0) {
        const sumLon = coords.reduce((acc, c) => acc + (Number(c?.[0]) || 0), 0);
        const sumLat = coords.reduce((acc, c) => acc + (Number(c?.[1]) || 0), 0);
        return [sumLat / coords.length, sumLon / coords.length];
      }
    }
    const firstGrave = allGraves[0];
    if (firstGrave?.geometry?.type === "Point" && Array.isArray(firstGrave.geometry.coordinates)) {
      const gCoords = firstGrave.geometry.coordinates as unknown as number[];
      return [Number(gCoords[1]), Number(gCoords[0])];
    }
    return undefined;
  }, [mapData, allGraves]);

  // Áp dụng bộ lọc
  const filteredGraves = useMemo(() => {
    return allGraves.filter((f) => {
      const props = (f.properties || {}) as GravePositionProperties;
      // Danh tính
      if (props.identityStatus && !identityFilters[props.identityStatus]) {
        return false;
      }
      // Trạng thái vị trí
      if (positionState !== "ALL" && props.positionState !== positionState) {
        return false;
      }
      // Tìm kiếm theo mã hoặc tên
      if (search) {
        const q = search.toLowerCase();
        const codeMatch = props.positionCode?.toLowerCase().includes(q);
        const nameMatch = props.martyrName?.toLowerCase().includes(q);
        if (!codeMatch && !nameMatch) return false;
      }
      return true;
    });
  }, [allGraves, identityFilters, positionState, search]);

  const filteredLayers = useMemo(() => {
    if (!mapData) return [];
    return mapData.layers.map((l) => {
      if (l.layerCode === "GRAVE_POSITION") {
        return {
          ...l,
          featureCollection: {
            ...l.featureCollection,
            features: filteredGraves,
          },
        };
      }
      return l;
    });
  }, [mapData, filteredGraves]);

  const selectedFeature = useMemo(() => {
    return allGraves.find((f) => f.id === selectedFeatureId) ?? null;
  }, [allGraves, selectedFeatureId]);

  // 1. Loading State
  if (loading) {
    return (
      <div className="main-content p-6">
        <LoadingSkeleton />
      </div>
    );
  }

  // 2. Forbidden State (403 ngoài data scope)
  if (isForbiddenError(error)) {
    return (
      <div className="main-content p-6">
        <ForbiddenState
          description="Bạn không có quyền truy cập sơ đồ nghĩa trang này hoặc nghĩa trang nằm ngoài phạm vi được phân quyền."
          onBack={() => {
            void navigate("/cemeteries");
          }}
        />
      </div>
    );
  }

  // 3. Error State
  if (error || !cemetery || !mapData) {
    const reqId = extractRequestId(error);
    return (
      <div className="main-content p-6">
        <ErrorState
          title="Không thể tải sơ đồ nghĩa trang"
          description="Đã xảy ra lỗi trong quá trình tải dữ liệu toạ độ GIS từ máy chủ."
          onRetry={() => {
            void fetchData();
          }}
          {...(reqId ? { requestId: reqId } : {})}
        />
      </div>
    );
  }

  // 4. Empty State (Chưa có sơ đồ toạ độ)
  if (mapData.layers.length === 0 || allGraves.length === 0) {
    return (
      <div className="main-content p-6 space-y-4">
        <div className="page-heading">
          <div>
            <h1 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--ncc-color-text-primary)", margin: 0 }}>{cemetery.name}</h1>
            <p style={{ fontSize: "0.75rem", color: "var(--ncc-color-text-secondary)", margin: "4px 0 0" }}>Mã nghĩa trang: {cemetery.cemeteryCode}</p>
          </div>
        </div>
        <div className="card" style={{ padding: "3rem", background: "var(--ncc-color-bg-surface)", border: "1px solid var(--ncc-color-border-default)", borderRadius: "var(--ncc-radius-lg)" }}>
          <EmptyState
            title="Chưa có sơ đồ toạ độ GIS"
            description="Nghĩa trang này chưa được thiết lập toạ độ ranh giới và phân lô phần mộ."
            action={
              readOnly ? null : (
                <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}/gis/import`}>
                  <Button type="primary">Nhập sơ đồ toạ độ (GeoJSON)</Button>
                </Link>
              )
            }
          />
        </div>
      </div>
    );
  }

  // 5. Ready State
  return (
    <div className="gis-page-root">
      {/* Chế độ nhúng: bỏ breadcrumb + tiêu đề (trang cha đã có), nút làm mới chuyển vào GisFilterBar.
          Chỉ render header khi đứng ở route độc lập (/cemeteries/:id/gis) */}
      {embedded ? null : (
        <div className="gis-page-header">
          <div className="gis-breadcrumb">
            <Link to="/cemeteries">Hồ sơ nghĩa trang</Link>
            <span>/</span>
            <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}`}>
              {cemetery.name}
            </Link>
            <span>/</span>
            <strong style={{ color: "var(--ncc-color-action-primary)" }}>Bản đồ toạ độ & Sơ đồ mộ</strong>
          </div>

          <div className="gis-header-row">
            <div className="gis-header-left">
              <h1 className="gis-header-title">{cemetery.name}</h1>
              <Tag color="success" style={{ fontWeight: 600 }}>
                Đang quản lý
              </Tag>
              <span style={{ color: "var(--ncc-color-border-default)" }}>|</span>
              <span className="gis-header-meta">
                Mã: <strong>{cemetery.cemeteryCode}</strong> · {cemetery.graveCounts ?? 0} vị trí mộ
              </span>
            </div>

            <div className="gis-header-actions">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  void fetchData();
                }}
              >
                Làm mới
              </Button>
              {readOnly ? null : (
                <>
                  <Button
                    icon={<CompassOutlined />}
                    onClick={() => setIsGeoRefModalOpen(true)}
                  >
                    Định vị mô hình 3D
                  </Button>
                  <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}/gis/import`}>
                    <Button type="primary" icon={<UploadOutlined />}>
                      Nhập sơ đồ toạ độ (GeoJSON)
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Grid Main Canvas + Right Rail */}
      <div className="gis-workspace" style={{ padding: embedded ? 0 : 16, gap: embedded ? 0 : 16 }}>
        {/* Main Panel */}
        <div className="gis-main-area" style={{ background: "var(--ncc-color-bg-surface)", border: "1px solid var(--ncc-color-border-default)", borderRadius: "var(--ncc-radius-lg)", overflow: "hidden" }}>
          <GisFilterBar
            search={search}
            onSearchChange={setSearch}
            identityFilters={identityFilters}
            onIdentityFilterChange={(k, v) => setIdentityFilters((s) => ({ ...s, [k]: v }))}
            positionState={positionState}
            onPositionStateChange={setPositionState}
            isTableView={isTableView}
            onToggleTableView={() => setIsTableView((v) => !v)}
            onRefresh={embedded ? () => { void fetchData(); } : undefined}
            onToggleDetail={embedded && selectedFeature ? () => setIsDrawerOpen((v) => !v) : undefined}
            isDetailOpen={isDrawerOpen}
          />

          {isTableView ? (
            <GisTableView
              graves={filteredGraves}
              selectedFeatureId={selectedFeatureId}
              onSelectFeature={(id) => {
                setSelectedFeatureId(id);
                setIsDrawerOpen(true);
              }}
              search={search}
            />
          ) : (
            <GisMapCanvas
              layers={filteredLayers}
              selectedGraveId={selectedFeatureId}
              onSelectGrave={(f) => {
                setSelectedFeatureId(f.id ?? null);
                if (f.id) {
                  setIsDrawerOpen(true);
                }
              }}
              readOnly={readOnly}
            />
          )}
        </div>

        {/* Right Rail: chỉ render inline khi KHÔNG embedded để bảo vệ layout ở 1280px */}
        {!embedded && (
          <GisDetailRail
            selectedFeature={selectedFeature}
            cemeteryId={cemeteryId}
            readOnly={readOnly}
            onOpenEditModal={() => setIsEditModalOpen(true)}
            onOpenHistory={() => alert("Mở Drawer Lịch sử")}
            onOpenQr={() => alert("Mã QR nội bộ")}
            onPrint={() => window.print()}
            {...(cemetery?.publicSlug
              ? { onOpenPublicPreview: () => setIsPublicModalOpen(true) }
              : {})}
          />
        )}
      </div>

      {/* Drawer Chi tiết vị trí mộ khi embedded */}
      {embedded && (
        <Drawer
          className="gis-detail-drawer"
          title={
            selectedFeature ? (
              <span style={{ fontSize: 13, fontWeight: 700 }}>
                Chi tiết vị trí mộ: {(selectedFeature.properties as GravePositionProperties)?.positionCode || selectedFeature.id}
              </span>
            ) : "Chi tiết vị trí mộ"
          }
          placement="right"
          width={380}
          open={isDrawerOpen && Boolean(selectedFeature)}
          onClose={() => setIsDrawerOpen(false)}
          destroyOnClose={false}
          mask={false}
        >
          <GisDetailRail
            selectedFeature={selectedFeature}
            cemeteryId={cemeteryId}
            readOnly={readOnly}
            onOpenEditModal={() => setIsEditModalOpen(true)}
            onOpenHistory={() => alert("Mở Drawer Lịch sử")}
            onOpenQr={() => alert("Mã QR nội bộ")}
            onPrint={() => window.print()}
            {...(cemetery?.publicSlug
              ? { onOpenPublicPreview: () => setIsPublicModalOpen(true) }
              : {})}
          />
        </Drawer>
      )}

      {/* Modal Sửa vị trí toạ độ */}
      {selectedFeature && (
        <EditPositionModal
          open={isEditModalOpen}
          positionId={selectedFeature.id ?? ""}
          graveCode={
            (selectedFeature.properties as GravePositionProperties)?.positionCode ||
            selectedFeature.id ||
            ""
          }
          version={1}
          initialPosition={{
            lon:
              selectedFeature.geometry?.type === "Point" &&
              Array.isArray(selectedFeature.geometry.coordinates)
                ? Number(selectedFeature.geometry.coordinates[0])
                : undefined,
            lat:
              selectedFeature.geometry?.type === "Point" &&
              Array.isArray(selectedFeature.geometry.coordinates)
                ? Number(selectedFeature.geometry.coordinates[1])
                : undefined,
            precisionMeters: (selectedFeature.properties as GravePositionProperties)
              ?.precisionMeters,
            source: (selectedFeature.properties as GravePositionProperties)?.sourceType,
            verificationStatus: (selectedFeature.properties as GravePositionProperties)
              ?.verificationStatus,
          }}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            void fetchData();
          }}
        />
      )}

      {/* Modal Xem trước công khai. CR-PUB3D-001 / DEC-100 D5: chỉ dựng khi nghĩa trang CÓ slug
          công khai — vừa thu hẹp kiểu cho `cemeterySlug`, vừa đúng ngữ nghĩa: không có slug thì
          không có gì để xem trước. */}
      {selectedFeature && cemetery && cemetery.publicSlug && (
        <PublicPreviewModal
          open={isPublicModalOpen}
          cemeterySlug={cemetery.publicSlug}
          graveCode={
            (selectedFeature.properties as GravePositionProperties)?.graveCode ||
            (selectedFeature.properties as GravePositionProperties)?.positionCode ||
            selectedFeature.id ||
            ""
          }
          onClose={() => setIsPublicModalOpen(false)}
        />
      )}

      {/* Modal Định vị mô hình 3D ranh giới nghĩa trang */}
      <ModelGeoReferenceModal
        open={isGeoRefModalOpen}
        cemeteryId={cemeteryId}
        cemeteryName={cemetery?.name || ""}
        cemeteryCode={cemetery?.cemeteryCode}
        initialCenter={mapCenter}
        onClose={() => setIsGeoRefModalOpen(false)}
        onSuccess={() => {
          void fetchData();
        }}
      />
    </div>
  );
}

